import React, { useEffect, useState } from "react";
import api, { brl, fmtDate, hojeISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";

const FREQ = [["diaria", "Diária (Seg a Sáb)"], ["diaria_full", "Diária Full TRT (Seg a Seg)"], ["semanal", "Semanal"], ["quinzenal", "Quinzenal"], ["mensal", "Mensal"]];
const FF = ({ l, children }) => <div className="space-y-1"><Label className="text-xs">{l}</Label>{children}</div>;

export const AcordoModal = ({ inst, onClose, onDone, onCreated }) => {
  const [parcelas, setParcelas] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ num_parcelas: 3, primeiro_vencimento: "", frequencia: "mensal", acrescimo: 0, observacao: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!inst) return;
    const d = new Date(); d.setDate(d.getDate() + 7);
    setF({ num_parcelas: 3, primeiro_vencimento: hojeISO(d), frequencia: "mensal", acrescimo: 0, observacao: "" });
    api.get(`/contracts/${inst.contrato_id}/overdue`).then((r) => {
      setParcelas(r.data);
      const s = {}; r.data.forEach((p) => { s[p.id] = p.status !== "pendente"; }); setSel(s);
    });
  }, [inst]);

  const chosen = parcelas.filter((p) => sel[p.id]);
  const saldo = chosen.reduce((a, p) => a + Number(p.valor || 0) - Number(p.valor_pago || 0), 0);
  const total = saldo + Number(f.acrescimo || 0);
  const vp = f.num_parcelas > 0 ? total / f.num_parcelas : 0;

  const submit = async () => {
    if (!chosen.length) return toast.error("Selecione ao menos uma parcela");
    setLoading(true);
    try {
      const { data } = await api.post(`/contracts/${inst.contrato_id}/renegociar`, {
        parcelas_ids: chosen.map((p) => p.id), num_parcelas: Number(f.num_parcelas),
        primeiro_vencimento: f.primeiro_vencimento, frequencia: f.frequencia,
        acrescimo: Number(f.acrescimo || 0), observacao: f.observacao,
      });
      if (data.pendente) { toast.success(data.mensagem || "Acordo registrado como pendente de análise"); onClose(); onDone(); return; }
      toast.success(`Acordo criado: ${data.novas.length}x de ${brl(data.valor_parcela)}`);
      onClose(); onDone();
      onCreated?.({ id: data.acordo_id, sign_token: data.sign_token, cliente_nome: inst.cliente_nome, cliente_telefone: data.cliente_telefone, num_parcelas: data.novas.length, total: data.total, assinatura: null });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar acordo"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!inst} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="acordo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Handshake className="w-5 h-5 gold-text" /> Acordo de Parcelas — {inst?.cliente_nome}</DialogTitle>
          <DialogDescription>Selecione as parcelas em aberto e defina o novo plano de pagamento.</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto rounded-lg border divide-y text-sm" data-testid="acordo-parcelas">
          {parcelas.map((p) => (
            <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
              <Checkbox checked={!!sel[p.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [p.id]: !!v }))} data-testid={`acordo-check-${p.id}`} />
              <span className="flex-1">Parcela #{p.numero} • vence {fmtDate(p.vencimento)}</span>
              <span className={`text-xs font-semibold ${p.status === "vencido" ? "text-red-600" : "text-slate-500"}`}>{p.status}</span>
              <span className="font-bold mono">{brl(Number(p.valor) - Number(p.valor_pago || 0))}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FF l="Novas parcelas"><Input type="number" min={1} value={f.num_parcelas} onChange={(e) => set("num_parcelas", e.target.value)} data-testid="acordo-num" /></FF>
          <FF l="Primeiro vencimento"><Input type="date" value={f.primeiro_vencimento} onChange={(e) => set("primeiro_vencimento", e.target.value)} data-testid="acordo-data" /></FF>
          <FF l="Frequência">
            <Select value={f.frequencia} onValueChange={(v) => set("frequencia", v)}>
              <SelectTrigger data-testid="acordo-freq"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQ.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </FF>
          <FF l="Acréscimo juros/multa (R$)"><Input type="number" value={f.acrescimo} onChange={(e) => set("acrescimo", e.target.value)} data-testid="acordo-acrescimo" /></FF>
        </div>
        <FF l="Observação do acordo"><Textarea rows={2} value={f.observacao} onChange={(e) => set("observacao", e.target.value)} data-testid="acordo-obs" /></FF>
        <div className="bg-onyx rounded-lg p-3 grid grid-cols-3 text-center text-white" data-testid="acordo-resumo">
          <div><div className="text-[10px] text-slate-400 uppercase">Saldo</div><div className="font-bold mono">{brl(saldo)}</div></div>
          <div><div className="text-[10px] text-slate-400 uppercase">Total acordo</div><div className="font-bold mono gold-text">{brl(total)}</div></div>
          <div><div className="text-[10px] text-slate-400 uppercase">Por parcela</div><div className="font-bold mono">{brl(vp)}</div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading || !chosen.length} className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold" data-testid="acordo-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gerar Acordo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
